import AES from 'crypto-js/aes';
import enc from 'crypto-js/enc-utf8';
import findIndex from 'lodash/findIndex';
import isEmpty from 'lodash/isEmpty';
import remove from 'lodash/remove';
import moment from 'moment';
import { ChangeEvent } from 'react';
import { Action, Analytics, BalanceState, CouponState, EventType, NavLinkInterface, OutcomesAndBlocks, PlaceBet } from 'shared/interface';
import { v4 as uuidv4 } from 'uuid';

import { LoginApi } from 'api';
import {
	CardGamesIconColored,
	EntertainmentsIcon,
	EsportsIcon,
	LiveIconColored,
	PromotionIcon,
	SportIconColored,
	VirtualIconColored,
} from 'assets/icons';
import { CouponData, CouponResultText, SlipType, TransactionDetails } from 'features/coupon/interface';
import { TFunction } from 'react-i18next';
import { addNotification } from 'shared/components/toast';
import {
	ACC_VERIFICATION_STATUS,
	AppTheme,
	APP_URL,
	BETS,
	customerAccountStatus,
	ENCRYPTION_KEY,
	ESPORTS,
	MOBILE_BREAKPOINT,
	SPORTS,
	THEME,
} from 'shared/constants/common';
import { BetMatchEvents, BetslipEvents, setDataLayer } from 'shared/constants/datalayer';

import BetMatchIcon from 'assets/images/betMatch.svg?react';
import MasterCardGreyBackgroundIcon from 'assets/images/paymentMethods/masterCardGreyBackground.svg?react';
import VisaGreyBackgroundIcon from 'assets/images/paymentMethods/visaGreyBackground.svg?react';
import { formatCouponHistoryDataByBlockId, getOutcomesAndBlocksAfterChange } from 'features/coupon/utility';
import { GamesState } from 'features/eventPreMatch/interface/sportPrematch';
import { ISportEvent } from 'features/sports/interface/interface';
import 'moment/dist/locale/pl';
import { ThunkDispatch } from 'redux-thunk';
import * as actionTypes from '../store/action-types';

import LiveIconDark from 'assets/images/bottomBar/live-icon-dark.svg?react';
import LiveIconBottom from 'assets/images/bottomBar/live-icon.svg?react';
import OfferIconDark from 'assets/images/bottomBar/offer-icon-dark.svg?react';
import OfferIcon from 'assets/images/bottomBar/offer-icon.svg?react';
import PromotionsIconDark from 'assets/images/bottomBar/promotions-icon-dark.svg?react';
import PromotionsIcon from 'assets/images/bottomBar/promotions-icon.svg?react';
import SpecialIconDark from 'assets/images/bottomBar/special-icon-dark.svg?react';
import SpecialIcon from 'assets/images/bottomBar/special-icon.svg?react';
import CouponsIconDark from 'assets/images/bottomBar/ticket-icon-dark.svg?react';
import CouponsIcon from 'assets/images/bottomBar/ticket-icon.svg?react';
import { OddsMainType, OddsType } from 'features/eventLive/interface/interface';
import { Bonus, Freebet } from 'features/offers/interface/offers.interface';
import { OffersState } from 'features/offers/interface/store.interface';
import { reactNativeEvent } from '../react-native/event';
import { getAnalyticalData } from './analytical';

export const createAction = (ACTION: string, data: any = null): Action => {
	return {
		type: ACTION,
		payload: data,
	};
};

export const findCookieValue = (name: string) => {
	const matchingCookies = document.cookie
		/*
		 Get all cookies as an array of lines in the form of
		 [
		   'cookie-name=cookie-value',
		   ' other-cookie-name=other-cookie-value',
	     ]
		 */
		.split(';')
		// Trim the lines to get rid of leading spaces
		.map((cookie) => cookie.trim())
		// Get the lines which start with the name we want
		.filter((cookie) => cookie.startsWith(`${name}=`))
		// For each line get the value after the equal sign
		.map((cookie) => cookie.substring(name.length + 1))
		/*
		 Decode the value, for example:
		 %7B%22expirationDate%22%3A1742478808042
		 to
		 {"expirationDate":1742478808042
		 */
		.map((value) => decodeURIComponent(value));

	// Return empty string if not found for backwards compatibility
	return matchingCookies[0] || '';
};

export const createCookie = (name: string, value: string, days?: number) => {
	let expires: any;
	if (days) {
		const date = new Date();
		date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
		expires = '; expires=' + date.toUTCString();
	} else {
		expires = '';
	}
	document.cookie = name + '=' + value + expires + '; path=/';
};

export const createSessionCookie = (value: string | undefined) => {
	const date = new Date();
	const time = date.getTime();
	const expireTime = time + 1000 * 30 * 60;

	date.setTime(expireTime);
	const expires = `; expires=${date.toUTCString()}`;
	document.cookie = `x-odds-session=${value}${expires}; path=/`;
};

export const getCookie = (name: string | undefined) => {
	const value = '; ' + document.cookie;
	const parts = value.split('; ' + name + '=');
	if (parts?.length === 2) {
		return parts?.pop()?.split(';').shift();
	} else return '';
};

export const updateCookie = (name: string | undefined) => {
	const cookie = getCookie(name);
	createSessionCookie(cookie);
};

export const eraseSessionCookie = () => {
	document.cookie = `x-odds-session=; expires=-1; path=/`;
};

export const isLoggedin = () => {
	// if (findCookieValue('x-odds-session')) {
	// 	return true;
	// }
	if (localStorage.getItem('userDetails')) {
		return true;
	}
	return false;
};

export const getTheme = (): AppTheme => {
	return (localStorage.getItem(THEME) as AppTheme) || AppTheme.AUTO;
};

export const setTheme = (appTheme: AppTheme) => {
	localStorage.setItem(THEME, appTheme);
};
export const getBalanceDetails = () => {
	const balanceData: any = localStorage.getItem('balanceData');
	const decryptedData = decryptData(balanceData);
	return decryptedData || {};
};

export const setBalanceDetails = (data: BalanceState) => {
	const encryptedData = encryptData(data);
	localStorage.setItem('balanceData', encryptedData);
};

export const getBetsDetail = () => {
	const bets: any = localStorage.getItem(BETS);

	const decryptedData = decryptData(bets);

	return decryptedData || {};
};

export const setBetsDetail = (bets: any) => {
	const encryptedData = encryptData(bets);
	localStorage.setItem(BETS, encryptedData);
};

export const getVSMBetsDetail = () => {
	const bets: any = localStorage.getItem('vsmBets');
	const decryptedData = decryptData(bets);
	if (isEmpty(decryptedData)) return [];
	return decryptedData || [];
};

export const setVSMBetsDetail = (bets: any) => {
	const encryptedData = encryptData(bets);
	localStorage.setItem('vsmBets', encryptedData);
};

export const OUTCOME_NAME_DRAW = 'X';

const excludeBet = (outcomesAndBlocks: OutcomesAndBlocks, propertyName: keyof PlaceBet, propertyValue: any): OutcomesAndBlocks => {
	const filteredBlocks = outcomesAndBlocks.blocks.map((block) => block.filter((bet) => bet[propertyName] !== propertyValue));
	const filteredOutcomes = outcomesAndBlocks.outcomes.filter((bet) => bet[propertyName] !== propertyValue);

	const outcomes = getOutcomesAndBlocksAfterChange(filteredBlocks, filteredOutcomes).outcomes;
	const blocks = getOutcomesAndBlocksAfterChange(filteredBlocks, filteredOutcomes).blocks;

	return { outcomes, blocks };
};

const excludeBetByOutcomeId = (outcomesAndBlocks: OutcomesAndBlocks, outcomeId: string): OutcomesAndBlocks => {
	return excludeBet(outcomesAndBlocks, 'outcomeId', outcomeId);
};

const excludeBetByEventId = (outcomesAndBlocks: OutcomesAndBlocks, eventId: number): OutcomesAndBlocks => {
	return excludeBet(outcomesAndBlocks, 'eventId', eventId);
};

const excludeBetByCombinationType = (outcomesAndBlocks: OutcomesAndBlocks, combinationType: number): OutcomesAndBlocks => {
	return excludeBet(outcomesAndBlocks, 'combinationType', combinationType);
};

const includeBet = (outcomesAndBlocks: OutcomesAndBlocks, itemToAdd: PlaceBet): OutcomesAndBlocks => ({
	...outcomesAndBlocks,
	outcomes: [...outcomesAndBlocks.outcomes, itemToAdd],
});

export const setAllBets = (
	outcomesAndBlocks: OutcomesAndBlocks,
	item: PlaceBet,
	dispatch: ThunkDispatch<{}, {}, Action>,
	coupon: CouponState,
	analytics: Analytics,
	t?: TFunction<'translation'>,
): OutcomesAndBlocks => {
	const allBets = [outcomesAndBlocks.outcomes, outcomesAndBlocks.blocks].flat(2);
	const replacedBets = analytics.replacedBets;
	const eventAlreadyIncludedInBetBuilder = allBets.some((bet) => bet.isBetBuilder && bet.eventId === item.eventId);
	if (eventAlreadyIncludedInBetBuilder) {
		if (t) {
			addNotification('warning', `${t('betBuilderDiffMarketWarning')}`);
		}
		return outcomesAndBlocks;
	}

	const outcomeAlreadyIncluded = allBets.some((bet) => bet.outcomeId === item.outcomeId);
	if (outcomeAlreadyIncluded) {
		return excludeBetByOutcomeId(outcomesAndBlocks, item.outcomeId);
	}

	const eventAlreadyIncluded = allBets.some((bet) => bet.eventId === item.eventId);
	if (!eventAlreadyIncluded) {
		return includeBet(outcomesAndBlocks, item);
	}

	const allowedSportIds = [SPORTS.SKI_JUMPING, SPORTS.F1, SPORTS.SPEEDWAY];
	if (!item.isLive || !allowedSportIds.includes(Number(item.sportId))) {
		dispatch(createAction(actionTypes.ANALYTICS_REPLACE_BET));
		setDataLayer(BetslipEvents.BET_REPLACE(getAnalyticalData(coupon), replacedBets));
		return includeBet(excludeBetByEventId(outcomesAndBlocks, item.eventId), item);
	}

	const gameAlreadyIncluded = allBets.map((bet) => Number(bet.gameType)).includes(Number(item.gameType));
	const combinationAlreadyIncluded =
		item.combinationType && allBets.map((bet) => Number(bet.combinationType)).includes(Number(item.combinationType));
	if (!gameAlreadyIncluded && !combinationAlreadyIncluded) {
		return includeBet(outcomesAndBlocks, item);
	}

	if (item.combinationType) {
		return includeBet(excludeBetByCombinationType(outcomesAndBlocks, item.combinationType), item);
	}

	return outcomesAndBlocks;
};

export const setAllBetsBetBuild = (bets: GamesState[], item: GamesState) => {
	if (bets.some((bet) => bet.outcomeId === item.outcomeId)) {
		return remove(bets, (bet) => bet.outcomeId !== item.outcomeId);
	} else {
		if (bets.some((bet) => bet.gameId === item.gameId)) {
			const index = findIndex(bets, { gameId: item.gameId });
			bets.splice(index, 1, item);
			return [...bets];
		} else {
			return [...bets, item];
		}
	}
};

export const getEnvironmentDetails = () => {
	const device = ['iPhone', 'iPad', 'iPod', 'Android'].filter((item) => navigator.userAgent.toLowerCase().indexOf(item.toLowerCase()) !== -1);

	const browser = ['Opera', 'Chrome', 'Safari', 'Firefox', 'MSIE'].filter(
		(item) => navigator.userAgent.toLowerCase().indexOf(item.toLowerCase()) !== -1,
	);

	return {
		channel: device.length > 0 ? 'mobile' : 'desktop',
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		browser: browser.length > 0 ? browser[0]!.toLowerCase() : 'unknown',
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		device: device.length > 0 ? device[0]!.toLowerCase() : 'desktop',
	};
};

function product_Range(a: number, b: number) {
	let prd = a,
		i = a;

	while (i++ < b) {
		prd *= i;
	}
	return prd;
}

export const getNumOfCombinations = (n: number, r: number) => {
	if (n === r) {
		return 1;
	} else {
		r = r < n - r ? n - r : r;
		return product_Range(r + 1, n) / product_Range(1, n - r);
	}
};

/**
 * function which returns formatted date
 * @param date
 */
export const formatDate = (date: any, format?: string, showRelativeDay?: boolean) => {
	if (!date) {
		return '';
	}

	if (showRelativeDay) {
		const today = moment().local().endOf('day');
		const tomorrow = moment().local().add(1, 'day').endOf('day');

		if (date < today) {
			return 'Dziś';
		}

		if (date < tomorrow) {
			return 'Jutro';
		}
	}

	return moment(date)
		.local()
		.locale('pl')
		.format(format || 'YYYY-MM-DD HH:mm:ss');
};

export const msToHMS = (ms: number) => {
	const seconds = moment.duration(ms).seconds();
	const minutes = moment.duration(ms).minutes();
	const hours = Math.trunc(moment.duration(ms).asHours());
	return [{ hours: ('00' + hours).slice(-2), minutes: ('00' + minutes).slice(-2), seconds: ('00' + seconds).slice(-2) }];
};

export const addSpacesForBankAccountNumber = (text: string, places: number) => {
	if (text) {
		text = text.replaceAll(' ', '');
		if (text.length > 2) {
			const firstTwoNumber = text.slice(0, 2);
			const otherNumber = addSpaces(text.slice(2, text.length), places);
			return `${firstTwoNumber} ${otherNumber}`;
		} else {
			return text;
		}
	} else {
		return '';
	}
};

export const addSpaces = (text: string, places: number) => {
	if (text) {
		text = text.replaceAll(' ', '');
		let formattedText = '';

		for (let i = 0; i < text.length; i++) {
			if (i === text.length) {
				formattedText += text[i];
			} else if (i % places === 0 && i !== 0) {
				formattedText += ' ' + text[i];
			} else {
				formattedText += text[i];
			}
		}

		return formattedText;
	} else {
		return '';
	}
};

export const peselAgeValidation = (pesel: string) => {
	const dobStr = getBirthDateFormPesel(pesel);
	const today = new Date();
	const birthDate = new Date(dobStr);

	let age = today.getFullYear() - birthDate.getFullYear();
	const m = today.getMonth() - birthDate.getMonth();
	if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
		age--;
	}

	if (age >= 18) return true;
	return false;
};

export const getBirthDateFormPesel = (pesel: string) => {
	let year = parseInt(pesel.substring(0, 2));
	let month = parseInt(pesel.substring(2, 4));
	const day = parseInt(pesel.substring(4, 6));

	// age validation algorithm
	// 20 is add to 2000
	// 40 is add to 2100
	// etc..
	if (month > 80) {
		year = year + 1800;
		month = month - 80;
	} else if (month > 60) {
		year = year + 2200;
		month = month - 60;
	} else if (month > 40) {
		year = year + 2100;
		month = month - 40;
	} else if (month > 20) {
		year = year + 2000;
		month = month - 20;
	} else {
		year += 1900;
	}

	const date = String(`${year}/${month}/${day}`);

	// template string for birthDate new Date
	return moment(date).format('YYYY-MM-DD');
};

export const peselValueValidation = (pesel: string) => {
	const weight = [1, 3, 7, 9, 1, 3, 7, 9, 1, 3];
	let sum = 0;
	const controlNumber = parseInt(pesel.substring(10, 11));

	for (let i = 0; i < weight.length; i++) {
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		sum += parseInt(pesel.substring(i, i + 1)) * weight[i]!;
	}
	sum = sum % 10;

	return (10 - sum) % 10 === controlNumber;
};

export const isIOS = () => {
	return ['iPad', 'iPhone', 'iPod'].includes(navigator.platform);
};

export const isIOSApp = navigator.userAgent.toLowerCase().includes('ios_app');
export const isAndroidNewApp = navigator.userAgent.toLowerCase().includes('app_betfan_v2');
export const isAndroid = navigator.userAgent.toLocaleLowerCase().indexOf('android') > -1;
export const isPWA = () => window.matchMedia('(display-mode: standalone)').matches || document.referrer.includes('android-app://');

export const getAndroidAppBuildNumber = () => {
	if (!isAndroidNewApp) {
		return -1;
	}

	const match = navigator.userAgent.match(/APP_BETFAN_V2\.(\d+)/);

	if (!match?.length) {
		return -1;
	}

	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	return parseInt(match[1]!, 10);
};

type RegistrationMethod = 'mobile-app-android' | 'mobile-app-ios' | 'mobile-platform' | 'web';

export enum RegistrationMethodCode {
	ANDROID_APP = 21,
	IOS_APP = 22,
	WEB_BROWSER = 1,
	MOBILE_BROWSER = 7,
	TERMINAL = 52,
}

export const getRegisterMethod = (): RegistrationMethod => {
	if (isAndroidNewApp) {
		return 'mobile-app-android';
	}
	if (isPWA() && isAndroid) {
		return 'mobile-app-android';
	}
	if (isMobile() && isIOSApp) {
		return 'mobile-app-ios';
	}
	if (isMobile()) {
		return 'mobile-platform';
	}

	return 'web';
};

export const getParticipants = (event: any): string[] => {
	if (!isEmpty(event.participants)) {
		return event.participants.map((participant: any) => participant.participantName);
	}
	let eventName = event.eventName;
	if (event.eventType === EventType.LIVE) {
		eventName = eventName.replace('(live)', '');
	}
	const regex = /(\([^()]*-[^()]*\))/;
	const eventNameAfterReplace = eventName.replace(regex, '');
	const eventNames = eventNameAfterReplace.split(' - ');
	if (!eventNames[1]) {
		return [eventName];
	} else {
		return eventNames.slice(0, 2);
	}
};

export const logout = async (coupon?: CouponState) => {
	eraseSessionCookie();

	const userDetails = getUserDetails();
	reactNativeEvent().send('LOGOUT');

	try {
		const response = await LoginApi.logOutUser();
		if (response && response.status === 200) {
			setDataLayer({
				event: 'logout-success',
				...userDetails,
			});
			if (!localStorage.getItem('action')) {
				coupon && coupon?.bets.outcomes.length > 0 && setDataLayer(BetslipEvents.BET_ABANDON(getAnalyticalData(coupon)));
			}
			localStorage.removeItem('userDetails');
			localStorage.removeItem('balanceData');
			localStorage.removeItem('action');
		}
		return response;
	} catch (err) {
		return null;
	}
};

export const getUpdatedBalanceData = async () => {
	try {
		const response = await LoginApi.keepAlive(isLoggedin());
		if (response && response.status === 200 && !response.data.isError && response.data.data && response.data.data.customerData) {
			const customerData = response.data.data.customerData;
			const balanceData = {
				...getBalanceDetails(),
				mainBalance: customerData.balance,
				bonusBalance: customerData.calculatedBonus,
				bonusBalanceList: customerData.bonusBalanceList,
				depositCounter: customerData.depositCounter,
			};
			return balanceData;
		} else {
			return getBalanceDetails();
		}
	} catch (err) {
		console.error(err);
		return getBalanceDetails();
	}
};

export const virtualSportsEventType = [
	EventType.VIRTUAL_HORSE_RACING,
	EventType.VIRTUAL_FOOTBALL,
	EventType.VIRTUAL_DOG_RACING,
	EventType.VIRTUAL_BASKETBALL,
];

export const getCouponMode = (t: any, couponData: CouponData): string => {
	if (couponData.slipType === null || couponData.slipType === undefined) {
		return t('cardGames');
	}

	const lengthOfBankerBets = couponData.transactionDetails.filter((detail: TransactionDetails) => detail.banker).length;
	const detailsWithoutBankerBets = couponData.transactionDetails.filter((detail: TransactionDetails) => !detail.banker);
	const lengthOfGroups = formatCouponHistoryDataByBlockId(detailsWithoutBankerBets).length;

	const akoBetTemplate = `${t('akoBetType')} ${couponData.transactionDetails.length}`;
	const systemBetTemplate = `${t('systemBetType')} (${couponData.slipType} z ${lengthOfGroups}) ${
		lengthOfBankerBets > 0 ? `+ ${lengthOfBankerBets} p` : ''
	}`;

	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	const isVirtualSports = virtualSportsEventType.includes(couponData.transactionDetails[0]!.eventType);
	if (isVirtualSports && couponData.transactionDetails.length === 1) {
		return t('virtualSports');
	}

	if (couponData.slipType === SlipType.SOLO) {
		return t('soloBetType');
	}

	if (couponData.slipType === SlipType.AKO) {
		return akoBetTemplate;
	}

	return systemBetTemplate;
};

export enum CouponDetailStatusCode {
	IN_GAME = 1,
	WON = 2,
	LOST = 3,
	CANCELLED = 4,
	CASHOUT = 5,
	CANCELLED_BY_ADMIN = 10,
}

export const getSlipStatusClassFromStatusCode = (statusCode: number) => {
	switch (statusCode) {
		case CouponDetailStatusCode.IN_GAME:
			return 'inGame';
		case CouponDetailStatusCode.LOST:
			return 'isLost';
		case CouponDetailStatusCode.WON:
		case CouponDetailStatusCode.CASHOUT:
			return 'isWon';
		case CouponDetailStatusCode.CANCELLED:
		case CouponDetailStatusCode.CANCELLED_BY_ADMIN:
			return 'isCancelled';
		default:
			return '';
	}
};

export const CouponStatusCodeToStatusMap = {
	[CouponDetailStatusCode.IN_GAME]: CouponResultText.PLACED,
	[CouponDetailStatusCode.WON]: CouponResultText.WON,
	[CouponDetailStatusCode.LOST]: CouponResultText.LOST,
	[CouponDetailStatusCode.CANCELLED]: CouponResultText.CANCELLED,
	[CouponDetailStatusCode.CASHOUT]: CouponResultText.CASHOUT,
	[CouponDetailStatusCode.CANCELLED_BY_ADMIN]: CouponResultText.CANCELLED,
};

export const getSlipStatusClassFromResultText = ({ resultText }: CouponData) => {
	switch (resultText) {
		case CouponResultText.PLACED:
			return 'inGame';
		case CouponResultText.LOST:
			return 'isLost';
		case CouponResultText.WON:
		case CouponResultText.CASHOUT:
			return 'isWon';
		case CouponResultText.CANCELLED:
			return 'isCancelled';
		default:
			return '';
	}
};

export const getCouponMetaType = (data: any): string => {
	if (data.isFreebet) return 'FREEBET';
	if (data.resultText === CouponResultText.CASHOUT) return 'CASHOUT';
	if (data.offerDefinitionId) return 'BONUS';
	return '';
};

export const adjustDecimals = (val: any): string => {
	return ((typeof val === 'string' ? parseFloat(val) : val) || 0).toFixed(2);
};

export const isMobile = () => {
	let check = false;
	(function (a) {
		if (
			/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
				a,
			) ||
			/1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw-(n|u)|c55\/|capi|ccwa|cdm-|cell|chtm|cldc|cmd-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc-s|devi|dica|dmob|do(c|p)o|ds(12|-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(-|_)|g1 u|g560|gene|gf-5|g-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd-(m|p|t)|hei-|hi(pt|ta)|hp( i|ip)|hs-c|ht(c(-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i-(20|go|ma)|i230|iac( |-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|-[a-w])|libw|lynx|m1-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|-([1-8]|c))|phil|pire|pl(ay|uc)|pn-2|po(ck|rt|se)|prox|psio|pt-g|qa-a|qc(07|12|21|32|60|-[2-7]|i-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h-|oo|p-)|sdk\/|se(c(-|0|1)|47|mc|nd|ri)|sgh-|shar|sie(-|m)|sk-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h-|v-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl-|tdg-|tel(i|m)|tim-|t-mo|to(pl|sh)|ts(70|m-|m3|m5)|tx-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas-|your|zeto|zte-/i.test(
				a.substr(0, 4),
			)
		)
			check = true;
	})(navigator.userAgent || navigator.vendor);

	if (window.innerWidth < MOBILE_BREAKPOINT) check = true;

	return check;
};

export const isTablet = () => {
	const userAgent = navigator.userAgent.toLowerCase();
	const isTablet = /(ipad|tablet|(android(?!.*mobile))|(windows(?!.*phone)(.*touch))|kindle|playbook|silk|(puffin(?!.*(IP|AP|WP))))/.test(
		userAgent,
	);
	return isTablet;
};

export const navLinks = (darkMode: boolean, categoryName: string) => [
	{
		name: 'Live',
		icon: <>{darkMode ? <LiveIconDark /> : <LiveIconBottom />}</>,
		link: APP_URL.live,
	},
	{
		name: 'Oferta',
		icon: <>{darkMode ? <OfferIconDark /> : <OfferIcon />}</>,
		link: APP_URL.sport,
	},
	{
		name: 'Kupony',
		icon: <>{darkMode ? <CouponsIconDark /> : <CouponsIcon />}</>,
		link: APP_URL.coupon,
	},
	{
		name: 'Promocje',
		icon: <>{darkMode ? <PromotionsIconDark /> : <PromotionsIcon />}</>,
		link: APP_URL.promotion,
	},
	{
		name: categoryName,
		icon: <>{darkMode ? <SpecialIconDark /> : <SpecialIcon />}</>,
		link: APP_URL.special,
	},
];

export const sideNavLinks: NavLinkInterface[] = [
	{
		name: 'live',
		icon: <LiveIconColored />,
		link: APP_URL.live,
	},
	{
		name: 'offer',
		icon: <SportIconColored />,
		link: APP_URL.sport,
	},
	{
		name: 'eSport',
		icon: <EsportsIcon />,
		link: APP_URL.eSport,
	},
	{
		name: 'virtualSports',
		icon: <VirtualIconColored />,
		link: APP_URL.virtualSports,
	},
	{
		name: 'cardGames',
		icon: <CardGamesIconColored />,
		link: APP_URL.cardGames,
	},
	{
		name: 'entertainment',
		icon: <EntertainmentsIcon />,
		link: APP_URL.entertainment,
	},
	{
		name: 'promotion',
		icon: <PromotionIcon />,
		link: APP_URL.promotion,
	},
	...(isMobile()
		? [
				{
					name: 'betMatch',
					icon: <BetMatchIcon />,
					link: APP_URL.betMatch,
					onClick: () => {
						setDataLayer(BetMatchEvents.DATALAYER_BETMATCH_START_2);
					},
				},
		  ]
		: []),
];

export const isSafari =
	navigator.vendor &&
	navigator.vendor.indexOf('Apple') > -1 &&
	navigator.userAgent &&
	navigator.userAgent.indexOf('CriOS') === -1 &&
	navigator.userAgent.indexOf('FxiOS') === -1;

const body = document.getElementById('body');

export const handleAddOverflow = () => {
	document.documentElement.classList.add('lock-position');
	body?.classList.add('lock-position');
};

export const handleRemoveOverflow = () => {
	body?.classList.remove('lock-position');
	document.documentElement.classList.remove('lock-position');
};

export const handleAddOnlyOverflow = () => {
	document.documentElement.classList.add('lock-overflow');
	body?.classList.add('lock-overflow');
};

export const handleRemoveOnlyOverflow = () => {
	body?.classList.remove('lock-overflow');
	document.documentElement.classList.remove('lock-overflow');
};

export const removeSpecialCharacter = (item: string) => {
	if (!item) {
		return '';
	}
	let result = item
		.trim()
		.toLowerCase()
		.replaceAll(/[&/\\!#,+()$~%.'’":*?<>{}]/g, '');
	result = replaceAccentedCharacter(result);
	result = result.replaceAll(' ', '-');
	return result.trim();
};

export const removeMultipleDash = (item: string) => {
	// eslint-disable-next-line no-useless-escape
	return item?.replace(/\-\-+/g, '-');
};

export const couponCardStatusCodeMap: any = {
	1: 'in-game-coupon',
	2: 'won-coupon',
	3: 'lost-coupon',
	4: 'canceled-coupon',
	5: 'cashout',
	10: 'canceled-by-admin',
};

export const encryptData = (data: any) => {
	const encryptedData = AES.encrypt(JSON.stringify(data), ENCRYPTION_KEY);
	return encryptedData.toString();
};

export const decryptData = (data: string | null) => {
	try {
		const bytes = AES.decrypt((data || '').toString(), ENCRYPTION_KEY);
		return JSON.parse(bytes.toString(enc) || '{}');
	} catch (error) {
		return JSON.parse('{}');
	}
};

const Latinise: any = {
	latin_map: {
		Á: 'A',
		Ă: 'A',
		Ắ: 'A',
		Ặ: 'A',
		Ằ: 'A',
		Ẳ: 'A',
		Ẵ: 'A',
		Ǎ: 'A',
		Â: 'A',
		Ấ: 'A',
		Ậ: 'A',
		Ầ: 'A',
		Ẩ: 'A',
		Ẫ: 'A',
		Ä: 'A',
		Ǟ: 'A',
		Ȧ: 'A',
		Ǡ: 'A',
		Ạ: 'A',
		Ȁ: 'A',
		À: 'A',
		Ả: 'A',
		Ȃ: 'A',
		Ā: 'A',
		Ą: 'A',
		Å: 'A',
		Ǻ: 'A',
		Ḁ: 'A',
		Ⱥ: 'A',
		Ã: 'A',
		Ꜳ: 'AA',
		Æ: 'AE',
		Ǽ: 'AE',
		Ǣ: 'AE',
		Ꜵ: 'AO',
		Ꜷ: 'AU',
		Ꜹ: 'AV',
		Ꜻ: 'AV',
		Ꜽ: 'AY',
		Ḃ: 'B',
		Ḅ: 'B',
		Ɓ: 'B',
		Ḇ: 'B',
		Ƀ: 'B',
		Ƃ: 'B',
		Ć: 'C',
		Č: 'C',
		Ç: 'C',
		Ḉ: 'C',
		Ĉ: 'C',
		Ċ: 'C',
		Ƈ: 'C',
		Ȼ: 'C',
		Ď: 'D',
		Ḑ: 'D',
		Ḓ: 'D',
		Ḋ: 'D',
		Ḍ: 'D',
		Ɗ: 'D',
		Ḏ: 'D',
		ǲ: 'D',
		ǅ: 'D',
		Đ: 'D',
		Ƌ: 'D',
		Ǳ: 'DZ',
		Ǆ: 'DZ',
		É: 'E',
		Ĕ: 'E',
		Ě: 'E',
		Ȩ: 'E',
		Ḝ: 'E',
		Ê: 'E',
		Ế: 'E',
		Ệ: 'E',
		Ề: 'E',
		Ể: 'E',
		Ễ: 'E',
		Ḙ: 'E',
		Ë: 'E',
		Ė: 'E',
		Ẹ: 'E',
		Ȅ: 'E',
		È: 'E',
		Ẻ: 'E',
		Ȇ: 'E',
		Ē: 'E',
		Ḗ: 'E',
		Ḕ: 'E',
		Ę: 'E',
		Ɇ: 'E',
		Ẽ: 'E',
		Ḛ: 'E',
		Ꝫ: 'ET',
		Ḟ: 'F',
		Ƒ: 'F',
		Ǵ: 'G',
		Ğ: 'G',
		Ǧ: 'G',
		Ģ: 'G',
		Ĝ: 'G',
		Ġ: 'G',
		Ɠ: 'G',
		Ḡ: 'G',
		Ǥ: 'G',
		Ḫ: 'H',
		Ȟ: 'H',
		Ḩ: 'H',
		Ĥ: 'H',
		Ⱨ: 'H',
		Ḧ: 'H',
		Ḣ: 'H',
		Ḥ: 'H',
		Ħ: 'H',
		Í: 'I',
		Ĭ: 'I',
		Ǐ: 'I',
		Î: 'I',
		Ï: 'I',
		Ḯ: 'I',
		İ: 'I',
		Ị: 'I',
		Ȉ: 'I',
		Ì: 'I',
		Ỉ: 'I',
		Ȋ: 'I',
		Ī: 'I',
		Į: 'I',
		Ɨ: 'I',
		Ĩ: 'I',
		Ḭ: 'I',
		Ꝺ: 'D',
		Ꝼ: 'F',
		Ᵹ: 'G',
		Ꞃ: 'R',
		Ꞅ: 'S',
		Ꞇ: 'T',
		Ꝭ: 'IS',
		Ĵ: 'J',
		Ɉ: 'J',
		Ḱ: 'K',
		Ǩ: 'K',
		Ķ: 'K',
		Ⱪ: 'K',
		Ꝃ: 'K',
		Ḳ: 'K',
		Ƙ: 'K',
		Ḵ: 'K',
		Ꝁ: 'K',
		Ꝅ: 'K',
		Ĺ: 'L',
		Ƚ: 'L',
		Ľ: 'L',
		Ļ: 'L',
		Ḽ: 'L',
		Ḷ: 'L',
		Ḹ: 'L',
		Ⱡ: 'L',
		Ꝉ: 'L',
		Ḻ: 'L',
		Ŀ: 'L',
		Ɫ: 'L',
		ǈ: 'L',
		Ł: 'L',
		Ǉ: 'LJ',
		Ḿ: 'M',
		Ṁ: 'M',
		Ṃ: 'M',
		Ɱ: 'M',
		Ń: 'N',
		Ň: 'N',
		Ņ: 'N',
		Ṋ: 'N',
		Ṅ: 'N',
		Ṇ: 'N',
		Ǹ: 'N',
		Ɲ: 'N',
		Ṉ: 'N',
		Ƞ: 'N',
		ǋ: 'N',
		Ñ: 'N',
		Ǌ: 'NJ',
		Ó: 'O',
		Ŏ: 'O',
		Ǒ: 'O',
		Ô: 'O',
		Ố: 'O',
		Ộ: 'O',
		Ồ: 'O',
		Ổ: 'O',
		Ỗ: 'O',
		Ö: 'O',
		Ȫ: 'O',
		Ȯ: 'O',
		Ȱ: 'O',
		Ọ: 'O',
		Ő: 'O',
		Ȍ: 'O',
		Ò: 'O',
		Ỏ: 'O',
		Ơ: 'O',
		Ớ: 'O',
		Ợ: 'O',
		Ờ: 'O',
		Ở: 'O',
		Ỡ: 'O',
		Ȏ: 'O',
		Ꝋ: 'O',
		Ꝍ: 'O',
		Ō: 'O',
		Ṓ: 'O',
		Ṑ: 'O',
		Ɵ: 'O',
		Ǫ: 'O',
		Ǭ: 'O',
		Ø: 'O',
		Ǿ: 'O',
		Õ: 'O',
		Ṍ: 'O',
		Ṏ: 'O',
		Ȭ: 'O',
		Ƣ: 'OI',
		Ꝏ: 'OO',
		Ɛ: 'E',
		Ɔ: 'O',
		Ȣ: 'OU',
		Ṕ: 'P',
		Ṗ: 'P',
		Ꝓ: 'P',
		Ƥ: 'P',
		Ꝕ: 'P',
		Ᵽ: 'P',
		Ꝑ: 'P',
		Ꝙ: 'Q',
		Ꝗ: 'Q',
		Ŕ: 'R',
		Ř: 'R',
		Ŗ: 'R',
		Ṙ: 'R',
		Ṛ: 'R',
		Ṝ: 'R',
		Ȑ: 'R',
		Ȓ: 'R',
		Ṟ: 'R',
		Ɍ: 'R',
		Ɽ: 'R',
		Ꜿ: 'C',
		Ǝ: 'E',
		Ś: 'S',
		Ṥ: 'S',
		Š: 'S',
		Ṧ: 'S',
		Ş: 'S',
		Ŝ: 'S',
		Ș: 'S',
		Ṡ: 'S',
		Ṣ: 'S',
		Ṩ: 'S',
		Ť: 'T',
		Ţ: 'T',
		Ṱ: 'T',
		Ț: 'T',
		Ⱦ: 'T',
		Ṫ: 'T',
		Ṭ: 'T',
		Ƭ: 'T',
		Ṯ: 'T',
		Ʈ: 'T',
		Ŧ: 'T',
		Ɐ: 'A',
		Ꞁ: 'L',
		Ɯ: 'M',
		Ʌ: 'V',
		Ꜩ: 'TZ',
		Ú: 'U',
		Ŭ: 'U',
		Ǔ: 'U',
		Û: 'U',
		Ṷ: 'U',
		Ü: 'U',
		Ǘ: 'U',
		Ǚ: 'U',
		Ǜ: 'U',
		Ǖ: 'U',
		Ṳ: 'U',
		Ụ: 'U',
		Ű: 'U',
		Ȕ: 'U',
		Ù: 'U',
		Ủ: 'U',
		Ư: 'U',
		Ứ: 'U',
		Ự: 'U',
		Ừ: 'U',
		Ử: 'U',
		Ữ: 'U',
		Ȗ: 'U',
		Ū: 'U',
		Ṻ: 'U',
		Ų: 'U',
		Ů: 'U',
		Ũ: 'U',
		Ṹ: 'U',
		Ṵ: 'U',
		Ꝟ: 'V',
		Ṿ: 'V',
		Ʋ: 'V',
		Ṽ: 'V',
		Ꝡ: 'VY',
		Ẃ: 'W',
		Ŵ: 'W',
		Ẅ: 'W',
		Ẇ: 'W',
		Ẉ: 'W',
		Ẁ: 'W',
		Ⱳ: 'W',
		Ẍ: 'X',
		Ẋ: 'X',
		Ý: 'Y',
		Ŷ: 'Y',
		Ÿ: 'Y',
		Ẏ: 'Y',
		Ỵ: 'Y',
		Ỳ: 'Y',
		Ƴ: 'Y',
		Ỷ: 'Y',
		Ỿ: 'Y',
		Ȳ: 'Y',
		Ɏ: 'Y',
		Ỹ: 'Y',
		Ź: 'Z',
		Ž: 'Z',
		Ẑ: 'Z',
		Ⱬ: 'Z',
		Ż: 'Z',
		Ẓ: 'Z',
		Ȥ: 'Z',
		Ẕ: 'Z',
		Ƶ: 'Z',
		Ĳ: 'IJ',
		Œ: 'OE',
		ᴀ: 'A',
		ᴁ: 'AE',
		ʙ: 'B',
		ᴃ: 'B',
		ᴄ: 'C',
		ᴅ: 'D',
		ᴇ: 'E',
		ꜰ: 'F',
		ɢ: 'G',
		ʛ: 'G',
		ʜ: 'H',
		ɪ: 'I',
		ʁ: 'R',
		ᴊ: 'J',
		ᴋ: 'K',
		ʟ: 'L',
		ᴌ: 'L',
		ᴍ: 'M',
		ɴ: 'N',
		ᴏ: 'O',
		ɶ: 'OE',
		ᴐ: 'O',
		ᴕ: 'OU',
		ᴘ: 'P',
		ʀ: 'R',
		ᴎ: 'N',
		ᴙ: 'R',
		ꜱ: 'S',
		ᴛ: 'T',
		ⱻ: 'E',
		ᴚ: 'R',
		ᴜ: 'U',
		ᴠ: 'V',
		ᴡ: 'W',
		ʏ: 'Y',
		ᴢ: 'Z',
		á: 'a',
		ă: 'a',
		ắ: 'a',
		ặ: 'a',
		ằ: 'a',
		ẳ: 'a',
		ẵ: 'a',
		ǎ: 'a',
		â: 'a',
		ấ: 'a',
		ậ: 'a',
		ầ: 'a',
		ẩ: 'a',
		ẫ: 'a',
		ä: 'a',
		ǟ: 'a',
		ȧ: 'a',
		ǡ: 'a',
		ạ: 'a',
		ȁ: 'a',
		à: 'a',
		ả: 'a',
		ȃ: 'a',
		ā: 'a',
		ą: 'a',
		ᶏ: 'a',
		ẚ: 'a',
		å: 'a',
		ǻ: 'a',
		ḁ: 'a',
		ⱥ: 'a',
		ã: 'a',
		ꜳ: 'aa',
		æ: 'ae',
		ǽ: 'ae',
		ǣ: 'ae',
		ꜵ: 'ao',
		ꜷ: 'au',
		ꜹ: 'av',
		ꜻ: 'av',
		ꜽ: 'ay',
		ḃ: 'b',
		ḅ: 'b',
		ɓ: 'b',
		ḇ: 'b',
		ᵬ: 'b',
		ᶀ: 'b',
		ƀ: 'b',
		ƃ: 'b',
		ɵ: 'o',
		ć: 'c',
		č: 'c',
		ç: 'c',
		ḉ: 'c',
		ĉ: 'c',
		ɕ: 'c',
		ċ: 'c',
		ƈ: 'c',
		ȼ: 'c',
		ď: 'd',
		ḑ: 'd',
		ḓ: 'd',
		ȡ: 'd',
		ḋ: 'd',
		ḍ: 'd',
		ɗ: 'd',
		ᶑ: 'd',
		ḏ: 'd',
		ᵭ: 'd',
		ᶁ: 'd',
		đ: 'd',
		ɖ: 'd',
		ƌ: 'd',
		ı: 'i',
		ȷ: 'j',
		ɟ: 'j',
		ʄ: 'j',
		ǳ: 'dz',
		ǆ: 'dz',
		é: 'e',
		ĕ: 'e',
		ě: 'e',
		ȩ: 'e',
		ḝ: 'e',
		ê: 'e',
		ế: 'e',
		ệ: 'e',
		ề: 'e',
		ể: 'e',
		ễ: 'e',
		ḙ: 'e',
		ë: 'e',
		ė: 'e',
		ẹ: 'e',
		ȅ: 'e',
		è: 'e',
		ẻ: 'e',
		ȇ: 'e',
		ē: 'e',
		ḗ: 'e',
		ḕ: 'e',
		ⱸ: 'e',
		ę: 'e',
		ᶒ: 'e',
		ɇ: 'e',
		ẽ: 'e',
		ḛ: 'e',
		ꝫ: 'et',
		ḟ: 'f',
		ƒ: 'f',
		ᵮ: 'f',
		ᶂ: 'f',
		ǵ: 'g',
		ğ: 'g',
		ǧ: 'g',
		ģ: 'g',
		ĝ: 'g',
		ġ: 'g',
		ɠ: 'g',
		ḡ: 'g',
		ᶃ: 'g',
		ǥ: 'g',
		ḫ: 'h',
		ȟ: 'h',
		ḩ: 'h',
		ĥ: 'h',
		ⱨ: 'h',
		ḧ: 'h',
		ḣ: 'h',
		ḥ: 'h',
		ɦ: 'h',
		ẖ: 'h',
		ħ: 'h',
		ƕ: 'hv',
		í: 'i',
		ĭ: 'i',
		ǐ: 'i',
		î: 'i',
		ï: 'i',
		ḯ: 'i',
		ị: 'i',
		ȉ: 'i',
		ì: 'i',
		ỉ: 'i',
		ȋ: 'i',
		ī: 'i',
		į: 'i',
		ᶖ: 'i',
		ɨ: 'i',
		ĩ: 'i',
		ḭ: 'i',
		ꝺ: 'd',
		ꝼ: 'f',
		ᵹ: 'g',
		ꞃ: 'r',
		ꞅ: 's',
		ꞇ: 't',
		ꝭ: 'is',
		ǰ: 'j',
		ĵ: 'j',
		ʝ: 'j',
		ɉ: 'j',
		ḱ: 'k',
		ǩ: 'k',
		ķ: 'k',
		ⱪ: 'k',
		ꝃ: 'k',
		ḳ: 'k',
		ƙ: 'k',
		ḵ: 'k',
		ᶄ: 'k',
		ꝁ: 'k',
		ꝅ: 'k',
		ĺ: 'l',
		ƚ: 'l',
		ɬ: 'l',
		ľ: 'l',
		ļ: 'l',
		ḽ: 'l',
		ȴ: 'l',
		ḷ: 'l',
		ḹ: 'l',
		ⱡ: 'l',
		ꝉ: 'l',
		ḻ: 'l',
		ŀ: 'l',
		ɫ: 'l',
		ᶅ: 'l',
		ɭ: 'l',
		ł: 'l',
		ǉ: 'lj',
		ſ: 's',
		ẜ: 's',
		ẛ: 's',
		ẝ: 's',
		ḿ: 'm',
		ṁ: 'm',
		ṃ: 'm',
		ɱ: 'm',
		ᵯ: 'm',
		ᶆ: 'm',
		ń: 'n',
		ň: 'n',
		ņ: 'n',
		ṋ: 'n',
		ȵ: 'n',
		ṅ: 'n',
		ṇ: 'n',
		ǹ: 'n',
		ɲ: 'n',
		ṉ: 'n',
		ƞ: 'n',
		ᵰ: 'n',
		ᶇ: 'n',
		ɳ: 'n',
		ñ: 'n',
		ǌ: 'nj',
		ó: 'o',
		ŏ: 'o',
		ǒ: 'o',
		ô: 'o',
		ố: 'o',
		ộ: 'o',
		ồ: 'o',
		ổ: 'o',
		ỗ: 'o',
		ö: 'o',
		ȫ: 'o',
		ȯ: 'o',
		ȱ: 'o',
		ọ: 'o',
		ő: 'o',
		ȍ: 'o',
		ò: 'o',
		ỏ: 'o',
		ơ: 'o',
		ớ: 'o',
		ợ: 'o',
		ờ: 'o',
		ở: 'o',
		ỡ: 'o',
		ȏ: 'o',
		ꝋ: 'o',
		ꝍ: 'o',
		ⱺ: 'o',
		ō: 'o',
		ṓ: 'o',
		ṑ: 'o',
		ǫ: 'o',
		ǭ: 'o',
		ø: 'o',
		ǿ: 'o',
		õ: 'o',
		ṍ: 'o',
		ṏ: 'o',
		ȭ: 'o',
		ƣ: 'oi',
		ꝏ: 'oo',
		ɛ: 'e',
		ᶓ: 'e',
		ɔ: 'o',
		ᶗ: 'o',
		ȣ: 'ou',
		ṕ: 'p',
		ṗ: 'p',
		ꝓ: 'p',
		ƥ: 'p',
		ᵱ: 'p',
		ᶈ: 'p',
		ꝕ: 'p',
		ᵽ: 'p',
		ꝑ: 'p',
		ꝙ: 'q',
		ʠ: 'q',
		ɋ: 'q',
		ꝗ: 'q',
		ŕ: 'r',
		ř: 'r',
		ŗ: 'r',
		ṙ: 'r',
		ṛ: 'r',
		ṝ: 'r',
		ȑ: 'r',
		ɾ: 'r',
		ᵳ: 'r',
		ȓ: 'r',
		ṟ: 'r',
		ɼ: 'r',
		ᵲ: 'r',
		ᶉ: 'r',
		ɍ: 'r',
		ɽ: 'r',
		ↄ: 'c',
		ꜿ: 'c',
		ɘ: 'e',
		ɿ: 'r',
		ś: 's',
		ṥ: 's',
		š: 's',
		ṧ: 's',
		ş: 's',
		ŝ: 's',
		ș: 's',
		ṡ: 's',
		ṣ: 's',
		ṩ: 's',
		ʂ: 's',
		ᵴ: 's',
		ᶊ: 's',
		ȿ: 's',
		ɡ: 'g',
		ᴑ: 'o',
		ᴓ: 'o',
		ᴝ: 'u',
		ť: 't',
		ţ: 't',
		ṱ: 't',
		ț: 't',
		ȶ: 't',
		ẗ: 't',
		ⱦ: 't',
		ṫ: 't',
		ṭ: 't',
		ƭ: 't',
		ṯ: 't',
		ᵵ: 't',
		ƫ: 't',
		ʈ: 't',
		ŧ: 't',
		ᵺ: 'th',
		ɐ: 'a',
		ᴂ: 'ae',
		ǝ: 'e',
		ᵷ: 'g',
		ɥ: 'h',
		ʮ: 'h',
		ʯ: 'h',
		ᴉ: 'i',
		ʞ: 'k',
		ꞁ: 'l',
		ɯ: 'm',
		ɰ: 'm',
		ᴔ: 'oe',
		ɹ: 'r',
		ɻ: 'r',
		ɺ: 'r',
		ⱹ: 'r',
		ʇ: 't',
		ʌ: 'v',
		ʍ: 'w',
		ʎ: 'y',
		ꜩ: 'tz',
		ú: 'u',
		ŭ: 'u',
		ǔ: 'u',
		û: 'u',
		ṷ: 'u',
		ü: 'u',
		ǘ: 'u',
		ǚ: 'u',
		ǜ: 'u',
		ǖ: 'u',
		ṳ: 'u',
		ụ: 'u',
		ű: 'u',
		ȕ: 'u',
		ù: 'u',
		ủ: 'u',
		ư: 'u',
		ứ: 'u',
		ự: 'u',
		ừ: 'u',
		ử: 'u',
		ữ: 'u',
		ȗ: 'u',
		ū: 'u',
		ṻ: 'u',
		ų: 'u',
		ᶙ: 'u',
		ů: 'u',
		ũ: 'u',
		ṹ: 'u',
		ṵ: 'u',
		ᵫ: 'ue',
		ꝸ: 'um',
		ⱴ: 'v',
		ꝟ: 'v',
		ṿ: 'v',
		ʋ: 'v',
		ᶌ: 'v',
		ⱱ: 'v',
		ṽ: 'v',
		ꝡ: 'vy',
		ẃ: 'w',
		ŵ: 'w',
		ẅ: 'w',
		ẇ: 'w',
		ẉ: 'w',
		ẁ: 'w',
		ⱳ: 'w',
		ẘ: 'w',
		ẍ: 'x',
		ẋ: 'x',
		ᶍ: 'x',
		ý: 'y',
		ŷ: 'y',
		ÿ: 'y',
		ẏ: 'y',
		ỵ: 'y',
		ỳ: 'y',
		ƴ: 'y',
		ỷ: 'y',
		ỿ: 'y',
		ȳ: 'y',
		ẙ: 'y',
		ɏ: 'y',
		ỹ: 'y',
		ź: 'z',
		ž: 'z',
		ẑ: 'z',
		ʑ: 'z',
		ⱬ: 'z',
		ż: 'z',
		ẓ: 'z',
		ȥ: 'z',
		ẕ: 'z',
		ᵶ: 'z',
		ᶎ: 'z',
		ʐ: 'z',
		ƶ: 'z',
		ɀ: 'z',
		ﬀ: 'ff',
		ﬃ: 'ffi',
		ﬄ: 'ffl',
		ﬁ: 'fi',
		ﬂ: 'fl',
		ĳ: 'ij',
		œ: 'oe',
		ﬆ: 'st',
		ₐ: 'a',
		ₑ: 'e',
		ᵢ: 'i',
		ⱼ: 'j',
		ₒ: 'o',
		ᵣ: 'r',
		ᵤ: 'u',
		ᵥ: 'v',
		ₓ: 'x',
	},
};

const replaceAccentedCharacter = (item: string) => {
	return item.replace(/[^A-Za-z0-9[\] ]/g, (a) => Latinise.latin_map[a] || a);
};

export const eventPageTitle = (event: { eventName: string; eventSport: string }, isLive?: boolean) => {
	const { eventName, eventSport } = event;

	let pageTitle = '';

	if (isLive) {
		pageTitle = `${eventName} ${eventSport}`;
	} else {
		pageTitle = eventName;
	}

	const pagePostScript = '- Kursy i zakłady bukmacherskie | BETFAN';

	const fullTitle = `${pageTitle} ${pagePostScript}`;

	return fullTitle;
};

export const eventPageDescription = (event: { categoryName: string; eventName: string; eventSport: string }) => {
	const { categoryName, eventName, eventSport } = event;
	return `Zagraj ${eventName} ${categoryName} ${eventSport} w BETFAN! Sprawdź najlepsze kursy i szeroką ofertę zakładów bukmacherskich.`;
};

export const checkMeta = (textObj: { categoryName?: string; eventName: string; eventSport: string }, callback: Function) => {
	return Object.keys(textObj).length > 0 && callback(textObj);
};

const ENTERTAINMENT = {
	ENTERTAINMENT: 2285,
};

const ESPORTS_ARRAY = Object.values(ESPORTS);

export const isEsport = (event: any) => {
	return event.treatAsSport === -100 || ESPORTS_ARRAY.includes(event.sportId);
};

export const isFieldValueValid = (field: string, errors: any, touched: any) => {
	return errors && errors[field];
};

export const formatPrice = (value: any) => {
	const parseValue = value.toString().replace(/[^\d.]|\.(?=.*\.)/g, '.');
	const splitValue = parseValue.split('.');
	const beforeDot = splitValue[0];
	const afterDot = splitValue[1] ? splitValue[1]?.slice(-2) : null;
	if (value.toString()?.slice(-2) !== '..') {
		if (afterDot) {
			return beforeDot + '.' + afterDot;
		} else {
			if ((value === '.' || value === ',') && !beforeDot) {
				return '';
			} else {
				return parseValue;
			}
		}
	} else {
		return parseValue.substring(0, parseValue.length - 1);
	}
};

export const checkWelcomeOfferStatus = (amountValue: number | string, balanceData: BalanceState) =>
	formatPrice(amountValue) >= 50 && !balanceData?.depositCounter;

export const updateUserDetails = (key: string, value: string | number) => {
	const userDetails: any = getUserDetails();
	setUserDetails({ ...userDetails, [key]: value });
};

export const getUserDetails = () => {
	const data = localStorage.getItem('userDetails');
	const decryptedData = decryptData(data);
	return decryptedData || {};
};

export const setUserDetails = (data: any) => {
	const encryptedData = encryptData(data);
	return Promise.resolve().then(function () {
		localStorage.setItem('userDetails', encryptedData);
	});
};

export const replaceDashWithSpace = (item: string) => {
	const result = item.replaceAll('-', ' ');
	return result;
};

export const sportParamsId = new Map<string, number>([
	['pilka-nozna', SPORTS.FOOTBALL],
	['soccer', SPORTS.FOOTBALL],
	['tenis', SPORTS.TENNIS],
	['tennis', SPORTS.TENNIS],
	['koszykowka-3x3', SPORTS.BASKETBALL_3X3],
	['koszykowka', SPORTS.BASKETBALL],
	['siatkowka', SPORTS.VOLLYBALL],
	['pilka-reczna', SPORTS.HANDBALL],
	['pilka-reczna-plazowa', SPORTS.HANDBALLBEACH],
	['f1--sporty-motorowe', SPORTS.F1],
	['f1-sporty-motorowe', SPORTS.F1],
	['zuzel', SPORTS.SPEEDWAY],
	['baseball', SPORTS.BASEBALL],
	['bowls', SPORTS.BOWLS],
	['biegi-narciarskie', SPORTS.CROSS_COUNTRY_SKIING],
	['skoki-narciarskie', SPORTS.SKI_JUMPING],
	['boks', SPORTS.BOXING],
	['mma', SPORTS.MMA],
	['darts', SPORTS.DARTS],
	['futbol-australijski', SPORTS.AUSSIE_RULES],
	['futbol-amerykanski', SPORTS.AM_FOOTBAL],
	['futsal', SPORTS.FUTSAL],
	['krykiet', SPORTS.CRICKET],
	['kolarstwo', SPORTS.CYCLING],
	['narciarstwo-alpejskie', SPORTS.SKIING],
	['pilka-plazowa', SPORTS.BEACH_SOCCER],
	['pesapallo', SPORTS.PESAPALLO],
	['rugby', SPORTS.RUGBY],
	['siatkowka-plazowa', SPORTS.BEACH_VOLLYBALL],
	['snooker', SPORTS.SNOOKER],
	['tenis-stolowy', SPORTS.TABLE_TENNIS],
	['zaklady-specjalne', SPORTS.ZAKLADY_SPECJALNE],
	['eurowizja', SPORTS.ZAKLADY_SPECJALNE],
	['hokej', SPORTS.ICE_HOCKEY],
	['unihokej', SPORTS.FLOORBALL],
	['hokej-na-trawie', SPORTS.FIELD_HOCKEY],
	['lekkoatletyka', SPORTS.ATHLETICS],
	['badminton', SPORTS.BADMINTON],
	['lyzwiarstwo-figurowe', SPORTS.FIGURE_SKATING],
	['pilka-wodna', SPORTS.WATER_POLO],
	['curling', SPORTS.CURLING],
	['bandy', SPORTS.BANDY],
	['biegi-narciarskie', SPORTS.CROSS_COUNTRY_SKIING],
	['lucznictwo', SPORTS.ARCHERY],
	['bobsleje', SPORTS.BOBSLEY],
	['kajakarstwo', SPORTS.CANOEING],
	['szachy', SPORTS.CHESS],
	['skoki-do-wody', SPORTS.WATERJUMPING],
	['jezdziectwo', SPORTS.HORSEMANSHIP],
	['szermierka', SPORTS.FENCING],
	['freestyle', SPORTS.FREESTYLE],
	['golf', SPORTS.GOLF],
	['judo', SPORTS.JUDO],
	['Kabaddi', SPORTS.KABADDI],
	['saneczkarstwo', SPORTS.LUGE],
	['piecioboj-nowoczesny', SPORTS.PENTATHLON],
	['kombinacja-norweska', SPORTS.NORDIC],
	['igrzyska-olimpijskie', SPORTS.OLYMPIC],
	['rajdy', SPORTS.RAID],
	['wioslarstwo', SPORTS.ROWING],
	['zeglarstwo', SPORTS.SAILING],
	['short-track', SPORTS.SHORTTRACK],
	['skateboarding', SPORTS.SKATEBOARDING],
	['skeleton', SPORTS.SKELETON],
	['snowboard', SPORTS.SNOWBOARD],
	['lyzwarstwo-szybkie', SPORTS.ICESKATING],
	['sporty-motorowe', SPORTS.MOTORSPORTS],
	['squash', SPORTS.SQUASH],
	['sumo', SPORTS.SUMO],
	['surfing', SPORTS.SURFING],
	['plywanie', SPORTS.SWIMMING],
	['taekwondo', SPORTS.TAEKWONDO],
	['triathlon', SPORTS.TRIATHLON],
	['podnoszenie-ciezarow', SPORTS.WEIGHTLIFTING],
	['wrestling', SPORTS.WRESTLING],
	['biathlon', SPORTS.BIATHLON],
	['wspinaczka-sportowa', SPORTS.CLIMBING],
	['igrzyska-europejskie', SPORTS.EUROPEAN_GAMES],
	['wbw', SPORTS.WBW],
	['wedkarstwo', SPORTS.FISHING],
	['csgo', ESPORTS.COUNTER_STRIKE],
	['cs2csgo', ESPORTS.COUNTER_STRIKE],
	['esport-counter-strike', ESPORTS.COUNTER_STRIKE],
	['counter-strike-2', ESPORTS.COUNTER_STRIKE],
	['dota', ESPORTS.DOTA2],
	['esport-dota', ESPORTS.DOTA2],
	['esport-nba2k', ESPORTS.NBA2K],
	['starcraft', ESPORTS.STAR_CRAFT],
	['lol', ESPORTS.LEAGUE_OF_LEGENDS],
	['esport-starcraft', ESPORTS.STAR_CRAFT],
	['lol', ESPORTS.LEAGUE_OF_LEGENDS],
	['esport-call-of-duty', ESPORTS.CALL_OF_DUTY],
	['hearthstone', ESPORTS.HEARTHSTONE],
	['rainbow-six', ESPORTS.RAINBOW_SIX],
	['entertainment', ENTERTAINMENT.ENTERTAINMENT],
	['rozrywka', ENTERTAINMENT.ENTERTAINMENT],
	['rozrywka-i-polityka', ENTERTAINMENT.ENTERTAINMENT],
]);

export const sportUrlFromSportId = new Map<number, string>([
	[SPORTS.FOOTBALL, 'pilka-nozna'],
	[SPORTS.TENNIS, 'tenis'],
	[SPORTS.BASKETBALL_3X3, 'koszykowka-3x3'],
	[SPORTS.BASKETBALL, 'koszykowka'],
	[SPORTS.VOLLYBALL, 'siatkowka'],
	[SPORTS.HANDBALL, 'pilka-reczna'],
	[SPORTS.HANDBALLBEACH, 'pilka-reczna-plazowa'],
	[SPORTS.F1, 'f1--sporty-motorowe'],
	[SPORTS.SPEEDWAY, 'zuzel'],
	[SPORTS.BASEBALL, 'baseball'],
	[SPORTS.BOWLS, 'bowls'],
	[SPORTS.CROSS_COUNTRY_SKIING, 'biegi-narciarskie'],
	[SPORTS.SKI_JUMPING, 'skoki-narciarskie'],
	[SPORTS.BOXING, 'boks'],
	[SPORTS.MMA, 'mma'],
	[SPORTS.DARTS, 'darts'],
	[SPORTS.AUSSIE_RULES, 'futbol-australijski'],
	[SPORTS.AM_FOOTBAL, 'futbol-amerykanski'],
	[SPORTS.FUTSAL, 'futsal'],
	[SPORTS.CRICKET, 'krykiet'],
	[SPORTS.CYCLING, 'kolarstwo'],
	[SPORTS.SKIING, 'narciarstwo-alpejskie'],
	[SPORTS.BEACH_SOCCER, 'pilka-plazowa'],
	[SPORTS.PESAPALLO, 'pesapallo'],
	[SPORTS.RUGBY, 'rugby'],
	[SPORTS.BEACH_VOLLYBALL, 'siatkowka-plazowa'],
	[SPORTS.SNOOKER, 'snooker'],
	[SPORTS.TABLE_TENNIS, 'tenis-stolowy'],
	[SPORTS.ZAKLADY_SPECJALNE, 'eurowizja'],
	[SPORTS.ICE_HOCKEY, 'hokej'],
	[SPORTS.FLOORBALL, 'unihokej'],
	[SPORTS.FIELD_HOCKEY, 'hokej-na-trawie'],
	[SPORTS.ATHLETICS, 'lekkoatletyka'],
	[SPORTS.BADMINTON, 'badminton'],
	[SPORTS.FIGURE_SKATING, 'lyzwiarstwo-figurowe'],
	[SPORTS.WATER_POLO, 'pilka-wodna'],
	[SPORTS.CURLING, 'curling'],
	[SPORTS.BANDY, 'bandy'],
	[SPORTS.CROSS_COUNTRY_SKIING, 'biegi-narciarskie'],
	[SPORTS.ARCHERY, 'lucznictwo'],
	[SPORTS.BOBSLEY, 'bobsleje'],
	[SPORTS.CANOEING, 'kajakarstwo'],
	[SPORTS.CHESS, 'szachy'],
	[SPORTS.WATERJUMPING, 'skoki-do-wody'],
	[SPORTS.HORSEMANSHIP, 'jezdziectwo'],
	[SPORTS.FENCING, 'szermierka'],
	[SPORTS.FREESTYLE, 'freestyle'],
	[SPORTS.GOLF, 'golf'],
	[SPORTS.JUDO, 'judo'],
	[SPORTS.KABADDI, 'Kabaddi'],
	[SPORTS.LUGE, 'saneczkarstwo'],
	[SPORTS.PENTATHLON, 'piecioboj-nowoczesny'],
	[SPORTS.NORDIC, 'kombinacja-norweska'],
	[SPORTS.OLYMPIC, 'igrzyska-olimpijskie'],
	[SPORTS.RAID, 'rajdy'],
	[SPORTS.ROWING, 'wioslarstwo'],
	[SPORTS.SAILING, 'zeglarstwo'],
	[SPORTS.SHORTTRACK, 'short-track'],
	[SPORTS.SKATEBOARDING, 'skateboarding'],
	[SPORTS.SKELETON, 'skeleton'],
	[SPORTS.SNOWBOARD, 'snowboard'],
	[SPORTS.ICESKATING, 'lyzwarstwo-szybkie'],
	[SPORTS.MOTORSPORTS, 'sporty-motorowe'],
	[SPORTS.SQUASH, 'squash'],
	[SPORTS.SUMO, 'sumo'],
	[SPORTS.SURFING, 'surfing'],
	[SPORTS.SWIMMING, 'plywanie'],
	[SPORTS.TAEKWONDO, 'taekwondo'],
	[SPORTS.TRIATHLON, 'triathlon'],
	[SPORTS.WEIGHTLIFTING, 'podnoszenie-ciezarow'],
	[SPORTS.WRESTLING, 'wrestling'],
	[SPORTS.BIATHLON, 'biathlon'],
	[SPORTS.CLIMBING, 'wspinaczka-sportowa'],
	[SPORTS.EUROPEAN_GAMES, 'igrzyska-europejskie'],
	[SPORTS.WBW, 'wbw'],
	[SPORTS.FISHING, 'wedkarstwo'],
	[ESPORTS.COUNTER_STRIKE, 'csgo'],
	[ESPORTS.COUNTER_STRIKE, 'cs2csgo'],
	[ESPORTS.COUNTER_STRIKE, 'esport-counter-strike'],
	[ESPORTS.COUNTER_STRIKE, 'counter-strike-2'],
	[ESPORTS.DOTA2, 'dota'],
	[ESPORTS.DOTA2, 'esport-dota'],
	[ESPORTS.NBA2K, 'esport-nba2k'],
	[ESPORTS.STAR_CRAFT, 'starcraft'],
	[ESPORTS.LEAGUE_OF_LEGENDS, 'lol'],
	[ESPORTS.STAR_CRAFT, 'esport-starcraft'],
	[ESPORTS.LEAGUE_OF_LEGENDS, 'lol'],
	[ESPORTS.CALL_OF_DUTY, 'esport-call-of-duty'],
	[ESPORTS.HEARTHSTONE, 'hearthstone'],
	[ESPORTS.RAINBOW_SIX, 'rainbow-six'],
	[ENTERTAINMENT.ENTERTAINMENT, 'rozrywka-i-polityka'],
]);

export const elementVisibility = (scrolledToBottom: boolean, scrollDir: string, element: string) => {
	const className = element;
	const visible = `${className}--visible`;
	const hidden = `${className}--hidden`;

	if (scrolledToBottom && scrollDir === 'down') {
		return visible;
	} else if (!scrolledToBottom && scrollDir === 'up') {
		return visible;
	} else if (!scrolledToBottom && scrollDir === 'down') {
		return hidden;
	} else {
		return visible;
	}
};

export const calcTotalBalance = (mainBalance: number | null, bonusBalance: number | null, freebets: Freebet[], bonusesQueued: Bonus[]): number => {
	const summarizedFreebetsBalance = freebets.reduce((acc, freebet) => acc + (freebet.freebetStake || 0), 0);
	const summarizedBonusesQueued = bonusesQueued.reduce((acc, bonus) => acc + (bonus.bonusValue || 0), 0);
	const main = mainBalance ?? 0;
	const bonus = bonusBalance ?? 0;
	const freebet = summarizedFreebetsBalance ?? 0;
	const bonusQueued = summarizedBonusesQueued ?? 0;

	return main + bonus + freebet + bonusQueued;
};

export const showAdvanceForTennis = (item: any) => {
	if (SPORTS.TENNIS) {
		return item.replace('50', 'AD').trim();
	}
};

export const matchResult = (result: string) => {
	if (SPORTS.TENNIS && result.includes('?')) {
		return result.replace('?-?', '0:0');
	} else {
		return result;
	}
};
export const getRemainingFreebetBalance = (freebetsActive: OffersState['freebetsActive']) => {
	return freebetsActive.reduce((acc, freebet) => acc + (freebet.freebetStake || 0), 0);
};

export const renderCreditCardIcon = (name: string) => {
	switch (name) {
		case 'VISA':
			return <VisaGreyBackgroundIcon />;
		case 'MasterCard':
			return <MasterCardGreyBackgroundIcon />;
		default:
			return <VisaGreyBackgroundIcon />;
	}
};

export const setDefaultCard = (data: any) => {
	if (data?.find((item: any) => item.defaultCard) === undefined) {
		return data[0];
	} else {
		return data?.find((item: any) => item.defaultCard);
	}
};

export function highlightSubstring(str: string, substr: string): string {
	const escapedSubstr = substr
		.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
		//Converts Polish diacritical characters to their regular expression equivalents.
		//By replacing a and ą with [aa], we create a regular expression that will match both a and ą in the text. Example: piłka/pilka = 'pi[lł]k[aą]';
		.replace(/[aą]/gi, '[aą]')
		.replace(/[cć]/gi, '[cć]')
		.replace(/[eę]/gi, '[eę]')
		.replace(/[lł]/gi, '[lł]')
		.replace(/[nń]/gi, '[nń]')
		.replace(/[oó]/gi, '[oó]')
		.replace(/[sś]/gi, '[sś]')
		.replace(/[zźż]/gi, '[zźż]');

	const regex = new RegExp(`(?:^|\\s)${escapedSubstr}`, 'gi');
	return str.replace(regex, `<b>$&</b>`);
}

export const disableInputNumbers = (e: ChangeEvent<HTMLInputElement>) => e.target.value.replace(/[0-9]/g, '');

export const disableInputAlphabets = (e: ChangeEvent<HTMLInputElement>) => e.target.value.replace(/[^0-9]/g, '');

export const getSelectedMarketTab = (currentTab: string, marketTypes: string[]) => {
	return marketTypes.includes(currentTab) ? currentTab : 'All';
};

// eslint-disable-next-line no-useless-escape
export const purifyStringFromBoxBrackets = (stringToPurify?: string) => stringToPurify?.replace(/[\[\]']+/g, '');

export const generateUserInitials = (firstName: string, lastName: string) => {
	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	return firstName[0]! + lastName[0]!;
};

export const createDynamicContentDataLayer = (Id: string, eventAction: any, eventAnalytics: string[]) => (event: Event) => {
	const target = event.target as HTMLElement;

	if (target.id === Id) {
		setDataLayer(eventAction(...eventAnalytics));
	}
};

export const weekDays = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
export const defaultStakeValues = [50, 100, 200, 500, 1000];
export const showModalForUnverifiedAccount = (userDetails: { verificationStatus: string; accountStatus: number; registeredOn: number }) => {
	if (!userDetails) return;
	const thirtyDaysInMillis = 30 * 24 * 60 * 60 * 1000;
	const currentTimestamp = Date.now();
	const timeDifference = currentTimestamp - userDetails.registeredOn;
	const showPopupDependingAccountStatus =
		userDetails.verificationStatus === ACC_VERIFICATION_STATUS.toCheck || userDetails.verificationStatus === ACC_VERIFICATION_STATUS.rejected;

	return (
		timeDifference >= thirtyDaysInMillis &&
		showPopupDependingAccountStatus &&
		userDetails.accountStatus === customerAccountStatus.UNVERIFIED_ACCOUNT
	);
};

export const moneyAmountDepositCommonValues = (rankingVip: number) => {
	switch (rankingVip) {
		case 5:
			return ['200', '500', '1000', '2000', '5000'];
		case 4:
			return ['100', '300', '500', '1000', '2000'];
		case 3:
			return ['100', '200', '500', '1000', '2000'];
		case 2:
		case 1:
		case 0:
			return ['100', '200', '300', '500', '1000'];
		default:
			return ['50', '100', '200', '500', '1000'];
	}
};

export const maskPeselNumber = (peselNumber: string) => {
	if (!peselNumber) return '';

	return peselNumber?.charAt(0) + '*'.repeat(peselNumber.length - 2) + peselNumber?.charAt(peselNumber.length - 1);
};

export const maskAccountNumber = (accountNumber: string) => {
	if (!accountNumber) return '';

	const length = accountNumber.length;

	const firstPart = accountNumber.slice(0, 2);
	const lastPart = accountNumber.slice(-4);
	const maskedAccountNumber = '*'.repeat(length - 6);
	return `${firstPart} ${maskedAccountNumber} ${lastPart}`;
};

export const formatNumberWithSpaces = (number: number) => {
	if (number == null) {
		return '';
	}
	const numStr = number.toString();
	const numParts = numStr.split('.');
	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	numParts[0] = numParts[0]!.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
	return numParts.join('.');
};

export const compareOdds = (previousData: ISportEvent[], currentData: ISportEvent[]) => {
	const previousOddsArray = previousData.reduce((acc: { outcomeId: number; outcomeOdds: number }[], event: ISportEvent) => {
		event?.games?.forEach((game: any) => {
			game?.outcomes.forEach((outcome: OddsType) => {
				acc.push({ outcomeId: outcome.outcomeId, outcomeOdds: outcome.outcomeOdds });
			});
		});
		return acc;
	}, []);

	return currentData?.flatMap((event: ISportEvent) => {
		return event?.games?.flatMap((game: any) => {
			return game?.outcomes?.map((outcome: OddsMainType) => {
				const previousOdds = previousOddsArray.find((odds: OddsMainType) => odds.outcomeId === outcome.outcomeId)?.outcomeOdds;
				if (previousOdds === undefined || outcome.outcomeOdds === previousOdds) {
					return { ...outcome, isUpdate: null };
				} else if (outcome.outcomeOdds > previousOdds) {
					return { ...outcome, isUpdate: true };
				} else if (outcome.outcomeOdds < previousOdds) {
					return { ...outcome, isUpdate: false };
				}
			});
		});
	});
};

export const isApplePay = (bankName: string) => {
	return bankName.toLowerCase().includes('apple');
};

export const isGooglePay = (bankName: string) => {
	return bankName.toLowerCase().includes('google');
};
export const resolveRandomUUID = () => {
	if (!window.crypto.randomUUID) {
		window.crypto.randomUUID = uuidv4;
	}
};
